import {API_BASE} from "./reports";
import buildHeaders from './fetchHeaders';

export async function getSegments(obj, signal, userId) {
    // Fulltext search endpoint
    const response = await fetch(`${API_BASE}/segments/search/fulltext`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        // Allow arbitrary properties including filters, pagination, includeSummary/includeContext
        body: JSON.stringify({...obj, userId}),
        signal,
    });
    if (!response.ok) {
        let message = 'Failed to fetch segments';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }
    const actualQuery = response.headers.get('X-Actual-Query');
    // New: pick up search id header
    const searchId = response.headers.get('X-Search-Id');
    const responseData = await response.json();
    // Backend now returns {meta: {segmentIds: [...], totalMatches: N}, data: [...]} format
    const segmentIds = responseData.meta?.segmentIds || [];
    const totalMatches = responseData.meta?.totalMatches || 0;
    return {results: responseData.data, actualQuery, searchId, segmentIds, totalMatches};
}

// New: vector (AI) search endpoint; same payload shape (uses `query`) and response mapping
export async function getSegmentsVector(obj, signal, userId) {
    const response = await fetch(`${API_BASE}/segments/search/ai`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({...obj, userId}),
        signal,
    });
    if (!response.ok) {
        let message = 'Failed to fetch segments (vector)';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }
    const actualQuery = response.headers.get('X-Actual-Query');
    const searchFocus = response.headers.get('X-Search-Focus');
    const searchId = response.headers.get('X-Search-Id');
    const responseData = await response.json();
    // Backend now returns {meta: {segmentIds: [...], totalMatches: N}, data: [...]} format
    const segmentIds = responseData.meta?.segmentIds || [];
    const totalMatches = responseData.meta?.totalMatches || 0;
    return {results: responseData.data, actualQuery, searchFocus, searchId, segmentIds, totalMatches};
}

// eslint-disable-next-line no-unused-vars
// New: convert free-form text into a boolean query via backend
export async function textToQuery(text, {signal} = {}) {
    if (!text || !text.trim()) return {originalText: text || '', query: ''};
    const response = await fetch(`${API_BASE}/segments/text-to-query`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({text}),
        signal,
    });

    if (!response.ok) {
        let message = 'Failed to convert text to query';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    return await response.json();
}

// call analytical-text-to-query endpoint for deeper/analytical conversion
export async function analyticalTextToQuery(text, {signal} = {}) {
    if (!text || !text.trim()) return {originalText: text || '', query: ''};
    const response = await fetch(`${API_BASE}/segments/analytical-text-to-query`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({text}),
        signal,
    });

    if (!response.ok) {
        let message = 'Failed to convert analytical text to query';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }

    return await response.json();
}

// New: fetch search summary by searchId
export async function getSearchSummary(searchId, {signal} = {}) {
    if (!searchId) throw new Error('searchId is required');
    const response = await fetch(`${API_BASE}/segments/search/summary`, {
        method: 'POST',
        headers: buildHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }),
        body: JSON.stringify({searchId}),
        signal,
    });
    if (!response.ok) {
        let message = 'Failed to fetch search summary';
        try {
            const errText = await response.text();
            if (errText) message += `: ${errText}`;
        } catch (_) {
        }
        throw new Error(message);
    }
    return await response.json();
}
